// ============================================================================
// CINEMATIC CONTRIBUTION OVERLAY - Wide avatar contribution cards
// Accepted contributions pushed to the cinematic screen. Emotion flags stripped.
// Cards fade in, linger for ~35s, then fade out. Stacks bottom-up.
// ============================================================================

import React, { useEffect, useState, useRef } from 'react';
import { useSecondScreenData } from '../../contexts/SecondScreenContext';
import type { CinematicContribution } from '../../types/second-screen.types';

const FADE_IN_MS = 800;
const FADE_OUT_MS = 2000;
const LINGER_MS = 35_000;
const SAFETY_CLEAR_MS = 38_000;

interface ContributionProps {
  contribution: CinematicContribution;
  onDismiss: (id: string) => void;
  avatarColor: string;
  avatarImageUrl?: string;
  avatarEmoji?: string;
}

const SingleContribution: React.FC<ContributionProps> = ({
  contribution,
  onDismiss,
  avatarColor,
  avatarImageUrl,
  avatarEmoji,
}) => {
  const [opacity, setOpacity] = useState(0);
  const fadeOutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dismissRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const raf = requestAnimationFrame(() => setOpacity(1));
    return () => cancelAnimationFrame(raf);
  }, []);

  useEffect(() => {
    fadeOutRef.current = setTimeout(() => setOpacity(0), LINGER_MS);
    dismissRef.current = setTimeout(() => onDismiss(contribution.id), SAFETY_CLEAR_MS);
    return () => {
      if (fadeOutRef.current) clearTimeout(fadeOutRef.current);
      if (dismissRef.current) clearTimeout(dismissRef.current);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClick = () => {
    setOpacity(0);
    setTimeout(() => onDismiss(contribution.id), FADE_OUT_MS);
  };

  const glowStyle = {
    boxShadow: `0 0 32px 8px ${avatarColor}55, 0 0 80px 16px ${avatarColor}22`,
    borderColor: avatarColor,
  };

  return (
    <div
      className="flex items-center gap-8 w-full cursor-pointer"
      style={{
        opacity,
        transition: `opacity ${opacity === 0 ? FADE_OUT_MS : FADE_IN_MS}ms ease`,
        pointerEvents: opacity > 0 ? 'auto' : 'none',
      }}
      onClick={handleClick}
    >
      {/* Avatar circle */}
      <div className="shrink-0 relative">
        <div
          className="w-28 h-28 rounded-full border-[4px] overflow-hidden transition-all duration-500"
          style={glowStyle}
        >
          {avatarImageUrl ? (
            <img src={avatarImageUrl} alt={contribution.avatarName} className="w-full h-full object-cover" />
          ) : (
            <div
              className="w-full h-full flex items-center justify-center text-5xl font-bold"
              style={{ backgroundColor: avatarColor + '33', color: '#fff' }}
            >
              {avatarEmoji || contribution.avatarName.charAt(0)}
            </div>
          )}
        </div>
        <div
          className="absolute inset-0 rounded-full animate-pulse-ring"
          style={{ borderWidth: 3, borderStyle: 'solid', borderColor: avatarColor + '80' }}
        />
      </div>

      {/* Text bubble */}
      <div className="flex-1 rounded-2xl bg-black/80 backdrop-blur-md border border-white/10 px-10 py-6">
        <p className="text-sm font-semibold mb-3 uppercase tracking-widest" style={{ color: avatarColor }}>
          {contribution.avatarName}
        </p>
        <p className="text-white text-3xl leading-relaxed font-medium">
          {contribution.text}
        </p>
      </div>
    </div>
  );
};

const CinematicContributionOverlay: React.FC = () => {
  const { activeCinematicContributions, clearCinematicContribution, state } = useSecondScreenData();

  if (activeCinematicContributions.length === 0) return null;

  return (
    <div className="fixed inset-x-0 top-0 z-[95] pointer-events-none flex flex-col gap-6 pt-14 px-[7.5%]">
      {activeCinematicContributions.map(contribution => {
        const avatar = state?.sessionAvatars.find(a => a.role === contribution.avatarRole);
        return (
          <div key={contribution.id} className="pointer-events-auto w-full">
            <SingleContribution
              contribution={contribution}
              onDismiss={clearCinematicContribution}
              avatarColor={avatar?.primaryColor || '#00c7c4'}
              avatarImageUrl={avatar?.imageUrl}
              avatarEmoji={avatar?.emoji}
            />
          </div>
        );
      })}
    </div>
  );
};

export default CinematicContributionOverlay;
